import type { PlannerTemplateFragment } from "@/graphql";
import toRRuleState from "@/helpers/rrules/toRRuleState";
import { toDate, toUnix } from "@/stories/utils/dates";
import { nanoid } from "nanoid";
import type { PlannerFormItem } from "@/types";
import {
  Approval_State,
  Container_Type,
  Plannertask_Assignee_Type,
  type PlannerSubtaskInput,
  type PlannerTaskAssigneeInput,
  type PlannerTaskCreateInput,
} from "@/graphql/generated-types";
import type { IFileState } from "@/interfaces";
import toRRuleInput from "@/helpers/rrules/toRRuleInput";
import { format } from "date-fns";

const toImageOrPictogram = (images: IFileState[] | undefined) => {
  let image: PlannerTaskCreateInput["image"] = undefined;
  let pictogramId: string | undefined = undefined;
  if (images) {
    const [firstItem] = images.length ? images : [undefined];
    if (firstItem?.externalSource === "pictogram") {
      pictogramId = firstItem.id;
    } else if (firstItem) {
      image = {
        body: firstItem.base64,
        container: Container_Type.Planner,
        title: firstItem.name,
        url: firstItem.url,
      };
    }
  }

  return { image, pictogramId };
};

const toPlannerTaskCreateInput = (
  form: PlannerFormItem,
): PlannerTaskCreateInput => {
  const { image, pictogramId } = toImageOrPictogram(form.images);
  let rset: PlannerTaskCreateInput["rset"] = undefined;

  if (form.rruleState) {
    rset = {
      excludedDates: [],
      rule: toRRuleInput(form.rruleState),
    };
  }

  // Assignees
  const { residents, departments, employees } = form;
  const assignees: PlannerTaskAssigneeInput[] = employees.map((id) => {
    return {
      id: id,
      type: Plannertask_Assignee_Type.Employee,
    };
  });

  if (residents.length > 0) {
    const [resident] = residents;
    assignees.push({ id: resident, type: Plannertask_Assignee_Type.Resident });
  }

  if (departments.length > 0) {
    const [department] = departments;
    assignees.push({
      id: department,
      type: Plannertask_Assignee_Type.Department,
    });
  }

  const subtasks: PlannerSubtaskInput[] = form.subtasks.map(
    (item, sortOrder) => {
      const { image, pictogramId } = toImageOrPictogram(
        item.image ? [item.image] : [],
      );
      return {
        id: item.id,
        image,
        pictogramId,
        sortOrder,
        title: item.title,
      };
    },
  );

  return {
    title: form.title,
    end: format(toDate(form.endDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    start: format(toDate(form.startDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    pictogramId,
    image,
    categoryId: form.category,
    description: form.description,
    rset,
    isRepeat: !!rset,
    approvalState: Approval_State.Unneeded,
    assignees,
    notificationSettings: [],
    subtasks,
    visibleForResident: false,
  };
};

const fromTemplateToPlannerFormItem = (
  template: PlannerTemplateFragment,
): PlannerFormItem => {
  const nextStartDate = new Date();

  if (template.start) {
    const [hours, minutes] = template.start.split(":");

    if (hours && minutes) {
      nextStartDate.setHours(
        Number.parseInt(hours),
        Number.parseInt(minutes),
        0,
        0,
      );
    }
  }

  const nextEndDate = new Date(nextStartDate);

  if (template.duration !== null && template.duration !== undefined) {
    nextEndDate.setMinutes(nextEndDate.getMinutes() + template.duration);
  } else {
    nextEndDate.setHours(nextEndDate.getHours() + 1);
  }

  const imageState = template.pictogram || template.image;

  return {
    title: template.title,
    category: template.category?.id,
    startDate: toUnix(nextStartDate),
    endDate: toUnix(nextEndDate),
    description: "",
    employees: [],
    departments: [],
    residents: [],
    images: imageState
      ? [
          {
            id: imageState.id,
            name: imageState.description || imageState.id,
            originalName: imageState.description || "",
            base64: imageState.source,
            url: imageState.source,
            type: "image",
            action: "upload",
            ...(template.pictogram
              ? { externalSource: "pictogram" as const }
              : {}),
          },
        ]
      : [],
    imageType: template.pictogram ? "pictogram" : "upload",
    rruleState: template.repeat?.rset
      ? {
          ...toRRuleState(template.repeat.rset),
          startDate: toUnix(nextStartDate),
        }
      : undefined,
    subtasks: (template.subtasks || []).map((subtask) => {
      return {
        id: nanoid(),
        title: subtask.title,
        mode: "edit",
      };
    }),
    validation: undefined,
  };
};

export default {
  toPlannerTaskCreateInput,
  fromTemplateToPlannerFormItem,
};
